import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useContact } from '../context/ContactContext';
import { API_URL } from '../config/api';
import './UalUs.css';

// ── Icons (inline SVG helpers) ──────────────────────────────────────────────
const Icon = ({ d, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const PlaneIcon   = () => <Icon d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />;
const HotelIcon   = () => <Icon d="M3 21V7l9-4 9 4v14M9 21v-8h6v8" />;
const CarIcon     = () => <Icon d="M5 17H3v-5l2-5h14l2 5v5h-2M5 17a2 2 0 104 0M15 17a2 2 0 104 0" />;
const CruiseIcon  = () => <Icon d="M3 17l2-8h14l2 8H3zM12 9V4M8 9V6M16 9V6M3 17c0 2.5 18 2.5 18 0" />;
const WifiIcon    = () => <Icon d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01" />;
const BagIcon     = () => <Icon d="M6 2h12l2 5H4L6 2zM3 7h18v14a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />;
const RefundIcon  = () => <Icon d="M3 12h18M3 12l4-4m-4 4l4 4M21 12l-4-4m4 4l-4 4" />;
const PetIcon     = () => <Icon d="M4.5 9.5a4 4 0 010-5M9 7a4.5 4.5 0 000-4M14 7a4.5 4.5 0 010-4M19.5 9.5a4 4 0 000-5M12 14a4 4 0 100 8 4 4 0 000-8z" />;
const StarIcon    = () => <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const ShieldIcon  = () => <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />;
const GlobeIcon   = () => <Icon d="M12 2a10 10 0 100 20A10 10 0 0012 2zM2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />;
const PhoneIcon   = () => <Icon d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .99h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />;
const CheckIcon   = () => <Icon d="M20 6L9 17l-5-5" />;
const ClockIcon   = () => <Icon d="M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2" />;
const MapPinIcon  = () => <Icon d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0zM12 10a1 1 0 100-2 1 1 0 000 2z" />;
const AppIcon     = () => <Icon d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />;

// ── Data ────────────────────────────────────────────────────────────────────
const TOPICS = [
  'Wi-Fi on Board', 'Change Flight', 'Airport Lounge', 'Traveling with Pets',
  'Rewards Program', 'Baggage Info', 'Refund Policy', 'Seat Upgrades',
  'Check-in Online', 'Special Assistance', 'Travel Advisories', 'Help Center'
];

const DEALS = [
  { city: 'New York', code: 'JFK', price: 149, img: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&q=70' },
  { city: 'Los Angeles', code: 'LAX', price: 179, img: 'https://images.unsplash.com/photo-1534190760961-74e8c1c5c3da?w=400&q=70' },
  { city: 'Chicago', code: 'ORD', price: 129, img: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=400&q=70' },
  { city: 'Miami', code: 'MIA', price: 199, img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=70' },
  { city: 'Denver', code: 'DEN', price: 109, img: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=400&q=70' },
  { city: 'Seattle', code: 'SEA', price: 159, img: 'https://images.unsplash.com/photo-1502175353174-a7a70e73b362?w=400&q=70' },
  { city: 'Cancún', code: 'CUN', price: 269, img: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=400&q=70' },
  { city: 'London', code: 'LHR', price: 449, img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=70' },
];

const SERVICES = [
  { icon: <WifiIcon />, title: 'In-Flight Wi-Fi', desc: 'Stay connected with high-speed Wi-Fi available on most flights.' },
  { icon: <StarIcon />, title: 'Rewards Program', desc: 'Earn miles on every trip. Redeem for flights, upgrades & more.' },
  { icon: <BagIcon />, title: 'Baggage Policy', desc: 'Understand what you can bring and fees that may apply.' },
  { icon: <ShieldIcon />, title: 'Travel Protection', desc: 'Protect your trip with flexible travel insurance options.' },
  { icon: <GlobeIcon />, title: 'Global Network', desc: 'Fly to 350+ destinations across 6 continents worldwide.' },
  { icon: <PetIcon />, title: 'Pet Travel', desc: 'Safe, comfortable options for traveling with your furry friends.' },
  { icon: <CheckIcon />, title: 'Easy Check-In', desc: 'Check in online, on mobile, or at our self-service kiosks.' },
  { icon: <ClockIcon />, title: '24/7 Support', desc: 'Our agents are available around the clock to assist you.' },
];

const DESTINATIONS = [
  { city: 'Paris', country: 'France', img: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&q=70' },
  { city: 'Tokyo', country: 'Japan', img: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=70' },
  { city: 'Cancún', country: 'Mexico', img: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=400&q=70' },
  { city: 'Rome', country: 'Italy', img: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&q=70' },
  { city: 'Sydney', country: 'Australia', img: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=400&q=70' },
];

const FOOTER_COLS = [
  { title: 'Book Travel', links: ['Book a Flight', 'Book a Hotel', 'Rent a Car', 'Cruise Vacations', 'Flight + Hotel Deals', 'Group Travel'] },
  { title: 'My Trips', links: ['Check In', 'Flight Status', 'View Reservations', 'Change Flight', 'Upgrade Seat', 'Request Refund'] },
  { title: 'Travel Info', links: ['Baggage Info', 'Airport Lounges', 'Wi-Fi Onboard', 'Seat Selection', 'Special Meals', 'Pet Travel'] },
  { title: 'Rewards', links: ['Join Program', 'Earn Miles', 'Redeem Miles', 'Credit Card', 'Partner Offers', 'Elite Status'] },
  { title: 'About Us', links: ['Our Story', 'Newsroom', 'Investor Relations', 'Sustainability', 'Careers', 'Contact Us'] },
];

// ── Component ────────────────────────────────────────────────────────────────
const UalUs = () => {
  const { contactSettings } = useContact();
  const navigate = useNavigate();
  const tfn = contactSettings?.tfn || '+1-888-859-0441';
  const tfnClean = tfn.replace(/[^0-9+]/g, '');

  const [activeTab, setActiveTab] = useState('flights');
  const [tripType, setTripType] = useState('roundtrip');
  const [from, setFrom] = useState('');
  const [fromCode, setFromCode] = useState('');
  const [to, setTo] = useState('');
  const [toCode, setToCode] = useState('');
  const [depart, setDepart] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [adults, setAdults] = useState('1');
  const [cabinClass, setCabinClass] = useState('economy');
  const [menuOpen, setMenuOpen] = useState(false);
  const [callPopupOpen, setCallPopupOpen] = useState(false);
  const [callPopupLabel, setCallPopupLabel] = useState('');

  // Airport autocomplete state
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const today = new Date().toISOString().split('T')[0];

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (fromRef.current && !fromRef.current.contains(e.target)) setFromSuggestions([]);
      if (toRef.current && !toRef.current.contains(e.target)) setToSuggestions([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced airport search
  const fetchAirports = useCallback(async (q, setSuggestions, setLoading) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/flights/airports?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setSuggestions(data.data || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fromTimer = useRef(null);
  const toTimer = useRef(null);

  const handleFromChange = (val) => {
    setFrom(val);
    setFromCode('');
    clearTimeout(fromTimer.current);
    fromTimer.current = setTimeout(() => fetchAirports(val, setFromSuggestions, setFromLoading), 300);
  };

  const handleToChange = (val) => {
    setTo(val);
    setToCode('');
    clearTimeout(toTimer.current);
    toTimer.current = setTimeout(() => fetchAirports(val, setToSuggestions, setToLoading), 300);
  };

  const selectFrom = (s) => {
    setFrom(`${s.cityName} (${s.iataCode})`);
    setFromCode(s.iataCode);
    setFromSuggestions([]);
  };

  const selectTo = (s) => {
    setTo(`${s.cityName} (${s.iataCode})`);
    setToCode(s.iataCode);
    setToSuggestions([]);
  };

  const openCallPopup = (label) => (e) => {
    e.preventDefault();
    setCallPopupLabel(label);
    setCallPopupOpen(true);
    setMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const origin = fromCode || from.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    const dest   = toCode   || to.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    if (!origin || !dest || !depart) return;
    const params = new URLSearchParams({
      from: origin,
      to: dest,
      departDate: depart,
      tripType,
      adults,
      cabinClass,
    });
    if (tripType === 'roundtrip' && returnDate) params.set('returnDate', returnDate);
    navigate(`/flight-results?${params.toString()}`);
  };

  return (
    <div className="ual-page">
      <Helmet>
        <title>United Airlines Flights – Book Cheap United Airlines Tickets | MFD Holidays</title>
        <meta name="description" content="Book United Airlines flights at the lowest fares. Find cheap United Airlines tickets, check-in online, manage reservations, and get 24/7 customer support. Best deals on United Airlines international & domestic flights." />
        <meta name="keywords" content="United Airlines, United Airlines flights, book United Airlines, United Airlines tickets, cheap United Airlines flights, United Airlines check in, United Airlines reservations, United Airlines baggage, United Airlines MileagePlus, United Airlines customer service, United Airlines booking, United Airlines deals, United Airlines international flights, UA flights, book flights online" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="United Airlines Flights – Book Cheap Tickets | MFD Holidays" />
        <meta property="og:description" content="Book United Airlines flights at the best prices. 24/7 customer support, flexible booking, and best fare guarantee." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mfdholidays.ca/ual/us" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="United Airlines Flights – Book Cheap Tickets | MFD Holidays" />
        <meta name="twitter:description" content="Book United Airlines flights at the best prices. 24/7 customer support available." />
        <link rel="canonical" href="https://mfdholidays.ca/ual/us" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          "name": "MFD Holidays",
          "description": "Book United Airlines flights at the lowest fares with 24/7 customer support.",
          "url": "https://mfdholidays.ca/ual/us",
          "telephone": tfn,
          "openingHours": "Mo-Su 00:00-23:59",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": tfn,
            "contactType": "customer support",
            "availableLanguage": "English",
            "hoursAvailable": "Mo-Su 00:00-23:59"
          },
          "sameAs": ["https://mfdholidays.ca"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "United Airlines Flights",
            "itemListElement": [
              {"@type": "Offer", "name": "United Airlines Domestic Flights", "description": "Book cheap United Airlines domestic flights across the USA"},
              {"@type": "Offer", "name": "United Airlines International Flights", "description": "Book United Airlines international flights worldwide"},
              {"@type": "Offer", "name": "United Airlines Business Class", "description": "Premium United Airlines business class seats at best prices"}
            ]
          }
        })}</script>
      </Helmet>

      {/* Skip link */}
      <a href="#book" className="ual-skip-link">Skip to Book</a>

      {/* ── Top Bar ── */}
      <div className="ual-topbar">
        <div className="ual-topbar-inner">
          <a href="/help">Help Center</a>
          <span className="ual-topbar-sep">|</span>
          <a href="/flights">Flight Status</a>
          <span className="ual-topbar-sep">|</span>
          <a href="#" onClick={() => window.location.href=`tel:${tfnClean}`}>Call Us: {tfn}</a>
          <span className="ual-topbar-sep">|</span>
          <span>🇺🇸 EN — USD</span>
        </div>
      </div>

      {/* ── Header ── */}
      <header className="ual-header">
        <div className="ual-header-inner">
          <a href="/" className="ual-logo-wrap">
            {/* Globe logo */}
            <svg className="ual-logo-globe" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="22" cy="22" r="20" stroke="#0066CC" strokeWidth="2" fill="#001a3a"/>
              <ellipse cx="22" cy="22" rx="8" ry="20" stroke="#0066CC" strokeWidth="1.5" fill="none"/>
              <line x1="2" y1="22" x2="42" y2="22" stroke="#0066CC" strokeWidth="1.5"/>
              <line x1="4" y1="14" x2="40" y2="14" stroke="#0066CC" strokeWidth="1"/>
              <line x1="4" y1="30" x2="40" y2="30" stroke="#0066CC" strokeWidth="1"/>
            </svg>

          </a>

          <ul className="ual-nav">
            <li><a href="#book" onClick={openCallPopup('Book a Flight')}>Book</a></li>
            <li><a href="#" onClick={openCallPopup('Flight Status')}>Flight Status</a></li>
            <li><a href="#" onClick={openCallPopup('Check-In')}>Check-In</a></li>
            <li><a href="#" onClick={openCallPopup('My Trips')}>My Trips</a></li>
            <li><a href="#" onClick={openCallPopup('MFD Rewards')}>MFD Rewards</a></li>
          </ul>

          <div className="ual-header-actions">
            <button className="ual-btn-signin">Sign In</button>
            <button className="ual-btn-join">Join Now</button>
          </div>

          {/* Hamburger */}
          <button
            className={`ual-hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ── Mobile Drawer ── */}
      <div className={`ual-mobile-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="ual-mobile-overlay" onClick={() => setMenuOpen(false)} />
        <nav className="ual-mobile-menu">
          <div className="ual-mobile-menu-header">
            <span>MFD Holidays</span>
            <button className="ual-mobile-menu-close" onClick={() => setMenuOpen(false)}>✕</button>
          </div>
          <ul className="ual-mobile-nav">
            <li><a href="#" onClick={openCallPopup('Book a Flight')}>✈️&nbsp; Book</a></li>
            <li><a href="#" onClick={openCallPopup('Flight Status')}>🔍&nbsp; Flight Status</a></li>
            <li><a href="#" onClick={openCallPopup('Check-In')}>🧳&nbsp; Check-In</a></li>
            <li><a href="#" onClick={openCallPopup('My Trips')}>📋&nbsp; My Trips</a></li>
            <li><a href="#" onClick={openCallPopup('MFD Rewards')}>⭐&nbsp; MFD Rewards</a></li>
            <li><a href="/contact" onClick={() => setMenuOpen(false)}>📞&nbsp; Contact Us</a></li>
            <li><a href="/help" onClick={() => setMenuOpen(false)}>❓&nbsp; Help Center</a></li>
          </ul>
          <div className="ual-mobile-auth">
            <button className="ual-btn-signin">Sign In</button>
            <button className="ual-btn-join">Join Now — It's Free</button>
          </div>
        </nav>
      </div>

      {/* ── Promo Banner ── */}
      <div className="ual-promo-banner">
        🎉 <strong>LIMITED-TIME OFFER:</strong> Earn up to 110,000 bonus miles + save at least 10% when booking with miles.&nbsp;
        <a href="/flights">Learn more →</a>
      </div>

      {/* ── Hero + Search ── */}
      <section className="ual-hero" id="book">
        <div className="ual-hero-bg">
          <h1 className="ual-hero-tagline">The World Is Waiting. Book Your Flight Today.</h1>
        </div>

        <div className="ual-search-container">
          <div className="ual-search-box">
            {/* Tabs */}
            <div className="ual-search-tabs">
              {[
                { key: 'flights', icon: '✈️', label: 'Flights' },
                { key: 'hotels', icon: '🏨', label: 'Hotels' },
                { key: 'cars', icon: '🚗', label: 'Cars' },
                { key: 'cruises', icon: '🚢', label: 'Cruises' },
              ].map(t => (
                <button
                  key={t.key}
                  className={`ual-search-tab ${activeTab === t.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Flight Search */}
            {activeTab === 'flights' && (
              <form className="ual-search-form" onSubmit={handleSearch}>
                <div className="ual-trip-type">
                  {[['roundtrip','Round Trip'],['oneway','One Way'],['multi','Multi-City']].map(([v,l]) => (
                    <label key={v}>
                      <input type="radio" name="triptype" value={v} checked={tripType===v} onChange={()=>setTripType(v)} />
                      {l}
                    </label>
                  ))}
                </div>
                <div className="ual-search-row">

                  {/* From */}
                  <div className="ual-field ual-autocomplete-wrap" ref={fromRef}>
                    <label>From</label>
                    <input
                      type="text"
                      placeholder="City or airport"
                      value={from}
                      onChange={e => handleFromChange(e.target.value)}
                      autoComplete="off"
                      required
                    />
                    {fromLoading && <div className="ual-ac-loading">Searching…</div>}
                    {fromSuggestions.length > 0 && (
                      <ul className="ual-ac-dropdown">
                        {fromSuggestions.map(s => (
                          <li key={s.iataCode} onMouseDown={() => selectFrom(s)}>
                            <span className="ual-ac-code">{s.iataCode}</span>
                            <span className="ual-ac-name">{s.cityName}{s.name !== s.cityName ? ` — ${s.name}` : ''}</span>
                            <span className="ual-ac-country">{s.countryName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* To */}
                  <div className="ual-field ual-autocomplete-wrap" ref={toRef}>
                    <label>To</label>
                    <input
                      type="text"
                      placeholder="City or airport"
                      value={to}
                      onChange={e => handleToChange(e.target.value)}
                      autoComplete="off"
                      required
                    />
                    {toLoading && <div className="ual-ac-loading">Searching…</div>}
                    {toSuggestions.length > 0 && (
                      <ul className="ual-ac-dropdown">
                        {toSuggestions.map(s => (
                          <li key={s.iataCode} onMouseDown={() => selectTo(s)}>
                            <span className="ual-ac-code">{s.iataCode}</span>
                            <span className="ual-ac-name">{s.cityName}{s.name !== s.cityName ? ` — ${s.name}` : ''}</span>
                            <span className="ual-ac-country">{s.countryName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="ual-field">
                    <label>Depart</label>
                    <input type="date" min={today} value={depart} onChange={e => setDepart(e.target.value)} required />
                  </div>
                  {tripType === 'roundtrip' && (
                    <div className="ual-field">
                      <label>Return</label>
                      <input type="date" min={depart || today} value={returnDate} onChange={e => setReturnDate(e.target.value)} />
                    </div>
                  )}
                  <div className="ual-field">
                    <label>Passengers</label>
                    <select value={adults} onChange={e => setAdults(e.target.value)}>
                      {[1,2,3,4,5,6,7,8,9].map(n => <option key={n} value={n}>{n} Passenger{n>1?'s':''}</option>)}
                    </select>
                  </div>
                  <div className="ual-field">
                    <label>Cabin</label>
                    <select value={cabinClass} onChange={e => setCabinClass(e.target.value)}>
                      <option value="economy">Economy</option>
                      <option value="premium_economy">Premium Economy</option>
                      <option value="business">Business</option>
                      <option value="first">First Class</option>
                    </select>
                  </div>
                  <button type="submit" className="ual-search-btn">Find Flights</button>
                </div>
              </form>
            )}

            {/* Hotels placeholder */}
            {activeTab === 'hotels' && (
              <div className="ual-search-form">
                <div className="ual-search-row">
                  <div className="ual-field" style={{flex:2}}>
                    <label>Destination</label>
                    <input type="text" placeholder="City, hotel, or landmark" />
                  </div>
                  <div className="ual-field">
                    <label>Check-In</label>
                    <input type="date" min={today} />
                  </div>
                  <div className="ual-field">
                    <label>Check-Out</label>
                    <input type="date" min={today} />
                  </div>
                  <div className="ual-field">
                    <label>Guests</label>
                    <select><option>1 Guest</option><option>2 Guests</option><option>3 Guests</option></select>
                  </div>
                  <button type="button" className="ual-search-btn">Find Hotels</button>
                </div>
              </div>
            )}

            {/* Cars placeholder */}
            {activeTab === 'cars' && (
              <div className="ual-search-form">
                <div className="ual-search-row">
                  <div className="ual-field" style={{flex:2}}>
                    <label>Pick-Up Location</label>
                    <input type="text" placeholder="Airport, city, or address" />
                  </div>
                  <div className="ual-field">
                    <label>Pick-Up Date</label>
                    <input type="date" min={today} />
                  </div>
                  <div className="ual-field">
                    <label>Return Date</label>
                    <input type="date" min={today} />
                  </div>
                  <button type="button" className="ual-search-btn">Find Cars</button>
                </div>
              </div>
            )}

            {/* Cruises placeholder */}
            {activeTab === 'cruises' && (
              <div className="ual-search-form">
                <div className="ual-search-row">
                  <div className="ual-field">
                    <label>Destination</label>
                    <select>
                      <option>Caribbean</option><option>Mediterranean</option><option>Alaska</option>
                      <option>Bahamas</option><option>Europe</option><option>Asia</option>
                    </select>
                  </div>
                  <div className="ual-field">
                    <label>Departure Month</label>
                    <select>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m=><option key={m}>{m} 2026</option>)}
                    </select>
                  </div>
                  <div className="ual-field">
                    <label>Duration</label>
                    <select><option>Any</option><option>3–5 nights</option><option>6–9 nights</option><option>10+ nights</option></select>
                  </div>
                  <button type="button" className="ual-search-btn">Find Cruises</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Popular Topics ── */}
      <section className="ual-topics">
        <div className="ual-topics-inner">
          <h2 className="ual-section-title">Popular Topics</h2>
          <div className="ual-topics-grid">
            {TOPICS.map(t => (
              <a key={t} href="#" className="ual-topic-chip">{t}</a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inline Call CTA 1 ── */}
      <div style={{background:'#0b3d91',padding:'18px 20px',textAlign:'center',display:'flex',flexWrap:'wrap',alignItems:'center',justifyContent:'center',gap:'16px'}}>
        <span style={{color:'#fff',fontSize:'16px',fontWeight:'600'}}>✈️ Need help booking your United Airlines flight?</span>
        <a href={`tel:${tfnClean}`} style={{background:'#ff3b30',color:'#fff',padding:'10px 24px',borderRadius:'8px',fontWeight:'bold',fontSize:'16px',textDecoration:'none',whiteSpace:'nowrap'}}>📞 Call Now: {tfn}</a>
      </div>

      {/* ── Flight Deals ── */}
      <section className="ual-deals">
        <div className="ual-deals-inner">
          <div className="ual-deals-header">
            <h2 className="ual-section-title">Popular Destinations</h2>
            <a href="/flights">View all deals →</a>
          </div>
          <div className="ual-cards-grid">
            {DEALS.map(d => (
              <a key={d.code} href={`/flights?to=${d.code}`} className="ual-deal-card">
                <img src={d.img} alt={d.city} className="ual-deal-img" loading="lazy" />
                <div className="ual-deal-info">
                  <div className="ual-deal-city">{d.city}</div>
                  <div className="ual-deal-from">Flights from</div>
                  <div className="ual-deal-price">${d.price} <span>one way</span></div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inline Call CTA 2 ── */}
      <div style={{background:'linear-gradient(to right,#001a3a,#0b3d91)',padding:'28px 20px',textAlign:'center'}}>
        <p style={{color:'#cce0ff',margin:'0 0 6px',fontSize:'14px',letterSpacing:'1px',textTransform:'uppercase'}}>Exclusive Unpublished Fares</p>
        <h3 style={{color:'#fff',fontSize:'22px',margin:'0 0 14px'}}>Save up to 70% on Business &amp; First Class Tickets</h3>
        <a href={`tel:${tfnClean}`} style={{display:'inline-block',background:'#ff6b00',color:'#fff',padding:'13px 30px',borderRadius:'8px',fontWeight:'bold',fontSize:'18px',textDecoration:'none',animation:'ual-pulse 1.5s infinite'}}>📞 {tfn} — Speak to an Agent</a>
      </div>

      {/* ── Services ── */}
      <section className="ual-services">
        <div className="ual-services-inner">
          <h2 className="ual-section-title">Products &amp; Services</h2>
          <div className="ual-services-grid">
            {SERVICES.map(s => (
              <div key={s.title} className="ual-service-card">
                <div className="ual-service-icon">{s.icon}</div>
                <div className="ual-service-title">{s.title}</div>
                <div className="ual-service-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Loyalty CTA ── */}
      <section className="ual-loyalty">
        <div className="ual-loyalty-inner">
          <h2>Join Our Rewards Program</h2>
          <p>
            Earn miles every time you fly — and on everyday purchases with our co-branded credit card.
            Redeem for flights, seat upgrades, hotel stays, and exclusive experiences.
          </p>
          <div className="ual-loyalty-btns">
            <a href="/flights" className="ual-btn-primary">Enroll Now — It's Free</a>
            <a href="/about" className="ual-btn-outline">Learn About Rewards</a>
          </div>
        </div>
      </section>

      {/* ── Destinations Grid ── */}
      <section className="ual-destinations">
        <div className="ual-destinations-inner">
          <h2 className="ual-section-title">Explore the World</h2>
          <div className="ual-dest-grid">
            {DESTINATIONS.map(d => (
              <a key={d.city} href={`/flights?to=${d.city}`} className="ual-dest-tile">
                <img src={d.img} alt={d.city} loading="lazy" />
                <div className="ual-dest-label">
                  {d.city}
                  <span>{d.country}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inline Call CTA 3 ── */}
      <div style={{background:'#ff3b30',padding:'20px',textAlign:'center',display:'flex',flexWrap:'wrap',alignItems:'center',justifyContent:'center',gap:'12px'}}>
        <span style={{color:'#fff',fontSize:'17px',fontWeight:'700'}}>🚨 Prices change fast — Lock your fare now!</span>
        <a href={`tel:${tfnClean}`} style={{background:'#fff',color:'#ff3b30',padding:'10px 22px',borderRadius:'8px',fontWeight:'bold',fontSize:'16px',textDecoration:'none',whiteSpace:'nowrap'}}>📞 {tfn}</a>
      </div>

      {/* ── Info Strips ── */}
      <section className="ual-strips">
        <div className="ual-strips-inner">
          <div className="ual-strip-item">
            <div className="ual-strip-icon"><CheckIcon /></div>
            <div className="ual-strip-text">
              <h4>Flexible Booking</h4>
              <p>Change or cancel most flights without a fee. Travel with confidence.</p>
            </div>
          </div>
          <div className="ual-strip-item">
            <div className="ual-strip-icon"><ShieldIcon /></div>
            <div className="ual-strip-text">
              <h4>Safe Travel Guarantee</h4>
              <p>Enhanced cleaning, HEPA filtration, and health-first policies on every flight.</p>
            </div>
          </div>
          <div className="ual-strip-item">
            <div className="ual-strip-icon"><GlobeIcon /></div>
            <div className="ual-strip-text">
              <h4>Global Network</h4>
              <p>Over 350 destinations across 6 continents — wherever life takes you.</p>
            </div>
          </div>
          <div className="ual-strip-item">
            <div className="ual-strip-icon"><PhoneIcon /></div>
            <div className="ual-strip-text">
              <h4>24/7 Expert Support</h4>
              <p>Live agents available around the clock for all your travel needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── App Promo ── */}
      <section className="ual-app-promo">
        <div className="ual-app-inner">
          <div className="ual-app-text">
            <h2>Download Our App</h2>
            <p>
              Check in, track your flight in real time, manage your bookings, and access
              your boarding pass — all from the palm of your hand.
            </p>
            <div className="ual-app-badges">
              <a href="#" className="ual-app-badge">🍎 App Store</a>
              <a href="#" className="ual-app-badge">▶️ Google Play</a>
            </div>
          </div>
          <div className="ual-app-phone">📱</div>
        </div>
      </section>

      {/* ── Customer Support Section ── */}
      <section className="ual-support">
        <div className="ual-support-inner">
          <div className="ual-support-header">
            <h2 className="ual-section-title">Need Help? We're Here 24/7</h2>
            <p className="ual-support-subtitle">Our United Airlines certified travel experts are ready to assist with bookings, changes, refunds, check-in, baggage, and more.</p>
          </div>
          <div className="ual-support-grid">
            <div className="ual-support-card" onClick={openCallPopup('Flight Booking Support')}>
              <div className="ual-support-img-wrap">
                <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=75" alt="Customer service agent" loading="lazy" />
                <div className="ual-support-overlay">
                  <span className="ual-support-call-badge">📞 Call Now</span>
                </div>
              </div>
              <div className="ual-support-card-body">
                <h3>Flight Booking</h3>
                <p>Book new United Airlines flights, find best deals, and choose your seats.</p>
                <a href={`tel:${tfnClean}`} className="ual-support-btn" onClick={e => e.stopPropagation()}>
                  <span>📞</span> {tfn}
                </a>
              </div>
            </div>
            <div className="ual-support-card" onClick={openCallPopup('Manage My Reservation')}>
              <div className="ual-support-img-wrap">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=75" alt="Support specialist" loading="lazy" />
                <div className="ual-support-overlay">
                  <span className="ual-support-call-badge">📞 Call Now</span>
                </div>
              </div>
              <div className="ual-support-card-body">
                <h3>Manage Reservations</h3>
                <p>Change flights, upgrade seats, add baggage or request a refund easily.</p>
                <a href={`tel:${tfnClean}`} className="ual-support-btn" onClick={e => e.stopPropagation()}>
                  <span>📞</span> {tfn}
                </a>
              </div>
            </div>
            <div className="ual-support-card" onClick={openCallPopup('Baggage & Check-In Help')}>
              <div className="ual-support-img-wrap">
                <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&q=75" alt="Travel assistance agent" loading="lazy" />
                <div className="ual-support-overlay">
                  <span className="ual-support-call-badge">📞 Call Now</span>
                </div>
              </div>
              <div className="ual-support-card-body">
                <h3>Baggage &amp; Check-In</h3>
                <p>Baggage policies, lost luggage, online check-in, and boarding pass help.</p>
                <a href={`tel:${tfnClean}`} className="ual-support-btn" onClick={e => e.stopPropagation()}>
                  <span>📞</span> {tfn}
                </a>
              </div>
            </div>
            <div className="ual-support-card" onClick={openCallPopup('United Airlines MileagePlus')}>
              <div className="ual-support-img-wrap">
                <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=75" alt="Rewards program specialist" loading="lazy" />
                <div className="ual-support-overlay">
                  <span className="ual-support-call-badge">📞 Call Now</span>
                </div>
              </div>
              <div className="ual-support-card-body">
                <h3>MileagePlus Rewards</h3>
                <p>Earn & redeem miles, elite status benefits, and partner rewards support.</p>
                <a href={`tel:${tfnClean}`} className="ual-support-btn" onClick={e => e.stopPropagation()}>
                  <span>📞</span> {tfn}
                </a>
              </div>
            </div>
          </div>
          {/* Big call strip inside support */}
          <div className="ual-support-cta">
            <div className="ual-support-cta-icon">📞</div>
            <div className="ual-support-cta-text">
              <strong>Speak with a United Airlines Travel Expert Now</strong>
              <span>No wait times · Best fares guaranteed · Available 24/7</span>
            </div>
            <a href={`tel:${tfnClean}`} className="ual-support-cta-btn">{tfn}</a>
          </div>
        </div>
      </section>

      {/* ── Call CTA ── */}
      <div className="ual-call-bar">
        <div className="ual-call-bar-inner">
          <div>
            <p>📞 Speak with a travel expert now</p>
            <span>Available 24/7 · Flights, hotels, vacation packages</span>
          </div>
          <a href={`tel:${tfnClean}`}>{tfn}</a>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="ual-footer">
        <div className="ual-footer-inner">
          <div className="ual-footer-grid">
            {FOOTER_COLS.map(col => (
              <div key={col.title} className="ual-footer-col">
                <h4>{col.title}</h4>
                <ul>
                  {col.links.map(link => (
                    <li key={link}><a href="#">{link}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="ual-footer-bottom">
            <p>© 2026 MFD Holidays, Inc. All rights reserved.</p>
            <div className="ual-footer-social">
              <a href="#" className="ual-social-btn" aria-label="Facebook">f</a>
              <a href="#" className="ual-social-btn" aria-label="Twitter">𝕏</a>
              <a href="#" className="ual-social-btn" aria-label="Instagram">📷</a>
              <a href="#" className="ual-social-btn" aria-label="LinkedIn">in</a>
            </div>
          </div>

          <div className="ual-footer-links">
            <a href="/terms">Terms &amp; Conditions</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="/help">Help Center</a>
            <a href="/contact">Contact Us</a>
            <a href="#">Accessibility</a>
            <a href="#">Contract of Carriage</a>
            <a href="#">Do Not Sell My Information</a>
            <a href="#">Cookie Preferences</a>
          </div>
        </div>
      </footer>
      {/* ── Floating Call Button ── */}
      <a href={`tel:${tfnClean}`} className="ual-float-call" aria-label="Call us now">
        <span className="ual-float-call-icon">📞</span>
        <span className="ual-float-call-text">
          <span className="ual-float-call-label">Call Us 24/7</span>
          <span className="ual-float-call-num">{tfn}</span>
        </span>
      </a>

      {/* ── United Airlines Call Popup ── */}
      {callPopupOpen && (
        <div className="ual-call-popup-overlay" onClick={() => setCallPopupOpen(false)}>
          <div className="ual-call-popup" onClick={e => e.stopPropagation()}>

            {/* Header bar */}
            <div className="ual-popup-header">
              <div className="ual-popup-ua-logo">
                <svg viewBox="0 0 64 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="ual-popup-ua-svg">
                  <text x="0" y="16" fontFamily="Arial" fontWeight="900" fontSize="18" fill="#fff" letterSpacing="1">UNITED</text>
                </svg>
                <span className="ual-popup-ua-globe">🌐</span>
              </div>
              <button className="ual-call-popup-close" onClick={() => setCallPopupOpen(false)} aria-label="Close">✕</button>
            </div>

            {/* Body */}
            <div className="ual-popup-body">
              <div className="ual-popup-badge">United Airlines — Official Support</div>
              <h3 className="ual-call-popup-title">{callPopupLabel}</h3>
              <p className="ual-call-popup-subtitle">
                Speak directly with a <strong>United Airlines specialist</strong>. Available <strong>24 hours, 7 days a week</strong> — no automated menus.
              </p>

              {/* Quick reasons */}
              <div className="ual-popup-reasons">
                <span>✈️ Book &amp; Change Flights</span>
                <span>🧳 Baggage &amp; Check-In</span>
                <span>⭐ MileagePlus Awards</span>
                <span>📋 Manage Reservation</span>
              </div>

              {/* Call button */}
              <a href={`tel:${tfnClean}`} className="ual-call-popup-btn">
                <span className="ual-call-popup-btn-label">📞 &nbsp;Call United Airlines Now</span>
                <span className="ual-call-popup-btn-number">{tfn}</span>
              </a>

              {/* Trust strip */}
              <div className="ual-popup-trust">
                <span>✔ Skip the hold</span>
                <span>✔ Instant booking</span>
                <span>✔ Free to call</span>
              </div>

              <p className="ual-call-popup-note">This is an independent travel service. United Airlines® is a registered trademark of United Airlines, Inc.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UalUs;
